/**
 * ZodiacOrb — the approved direction, assembled.
 *
 * The SVG astrolabe ring (fine weight) wrapped around LatticeCore - the orb
 * rebuilt as structure rather than a swarm. Drawn frame + live Three.js core,
 * which is the shipping architecture rather than a composite of it.
 *
 * Division of labour, per operator ruling:
 *   ring  -> calendar only. Never encodes thinking / listening / speaking.
 *   core  -> carries all state. Every orb prop is passed straight through.
 *
 * The shipped hero orb since 2026-09-02
 * (spec docs/superpowers/specs/2026-09-02-orb-reface-integration-design.md).
 * Renders inside the same .orb-hero-wrapper / .orb-hero-glow chrome the old
 * orb used, so the ambient glow behind the orb survives the swap.
 *
 * Sizing: CSS owns it (--orb-size-hero, fluid). With no `size` prop the
 * component measures its host with a ResizeObserver, rounds to 4px, and
 * applies on the next frame — so a window drag never rebuilds the
 * ~800-element ring per resize event. `size` stays available as an override.
 */
import React, { useEffect, useRef, useMemo, useState } from 'react';
import LatticeCore from './LatticeCore.jsx';
import { buildRing, solarLongitude, signOf, dayAngle } from './zodiacRing.js';
import './orb.css';
import './zodiac.css';

// Clear radius left for the core, in the ring's 400-unit viewBox. This is the
// core-to-frame ratio that sat unresolved at an arbitrary 44% for three rounds;
// 104/200 = 52% of the ring radius, which lets the core read as the subject
// while the graduation bands still have room to be legible.
const CORE_R = 104;

// Only used when the environment has no ResizeObserver (old Safari, tests).
const FALLBACK_SIZE = 480;

export default function ZodiacOrb({
  isThinking = false,
  isSpeaking = false,
  isListening = false,
  isError = false,
  theme = 'dark',
  onOrbClick,
  size: sizeProp,
}) {
  const hostRef = useRef(null);
  const ringRef = useRef(null);
  const [measured, setMeasured] = useState(0);
  const size = sizeProp ?? measured;

  useEffect(() => {
    if (sizeProp != null) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;
    if (typeof ResizeObserver === 'undefined') { setMeasured(FALLBACK_SIZE); return undefined; }
    let raf = 0;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width / 4) * 4;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setMeasured((prev) => (prev === w ? prev : w)));
    });
    ro.observe(host);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [sizeProp]);

  // One degree per day: rotation is a pure function of the date, recomputed on
  // mount only. No animation, no settle-on-load - that was the explicit ruling.
  const lon = useMemo(() => solarLongitude(new Date()), []);

  useEffect(() => {
    const host = ringRef.current;
    if (!host || !size) return undefined;
    host.innerHTML = '';
    host.appendChild(buildRing(size, lon, CORE_R, theme));

    // The sky moves a degree a day and never needs re-drawing while mounted.
    // The day marker moves 15 degrees an hour, so it ticks on the same 60s
    // cadence as the greeting's clock - and only its transform is touched,
    // never the ~800-element ring around it.
    const tick = () => {
      const hand = host.querySelector('[data-day-hand]');
      if (hand) hand.setAttribute('transform', `rotate(${dayAngle(new Date())} 200 200)`);
    };
    const id = setInterval(tick, 60_000);
    return () => { clearInterval(id); host.innerHTML = ''; };
  }, [size, lon, theme]);

  // The core occupies CORE_R of the viewBox's 200-unit radius. Size it directly
  // rather than scale-transforming a fixed box: the transform approach left the
  // canvas both undersized and off-centre.
  const coreSize = Math.round(size * (CORE_R / 200) * 0.94);

  return (
    <div className="orb-hero-wrapper">
      <div className={`orb-glow-bg orb-hero-glow${isListening ? ' orb-glow-bg--listening' : ''}`} />
      <div
        ref={hostRef}
        className="zodiac-orb"
        style={sizeProp != null ? { width: sizeProp, height: sizeProp } : undefined}
        title={`Sun in ${signOf(lon)}`}
      >
        <div ref={ringRef} className="zodiac-orb-ring" aria-hidden="false" />
        <div className="zodiac-orb-core">
          {size > 0 && (
            <LatticeCore
              isThinking={isThinking}
              isSpeaking={isSpeaking}
              isListening={isListening}
              isError={isError}
              theme={theme}
              onOrbClick={onOrbClick}
              size={coreSize}
            />
          )}
        </div>
      </div>
    </div>
  );
}
